import * as React from 'react';
import {
    Button,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    useMediaQuery,
    type Theme,
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
    useListSortContext,
    useTranslate,
    useTranslateLabel,
} from 'react-admin';
import { sortFieldBaseName } from '@wwf/sdk';

const inverseOrder = (order: string) => (order === 'ASC' ? 'DESC' : 'ASC');

export const MemberSortButton = () => {
    const { resource, sort, setSort } = useListSortContext();
    const translate = useTranslate();
    const translateLabel = useTranslateLabel();
    const isXSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('sm')
    );
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const fieldLabel = translateLabel({
        resource,
        source: sortFieldBaseName(sort.field),
    });
    const buttonLabel = translate('ra.sort.sort_by', {
        field: fieldLabel,
        field_lower_first:
            typeof fieldLabel === 'string'
                ? fieldLabel.charAt(0).toLowerCase() + fieldLabel.slice(1)
                : undefined,
        order: translate(`ra.sort.${sort.order}`),
        _: 'ra.sort.sort_by',
    });

    const handleChangeSort = (
        event: React.MouseEvent<HTMLLIElement, MouseEvent>
    ) => {
        const field = event.currentTarget.dataset.sort;
        if (!field) {
            throw new Error(
                '<MemberSortButton> MenuItems should have a data-sort attribute'
            );
        }
        setSort({
            field,
            order: field === sort.field ? inverseOrder(sort.order) : 'ASC',
        });
        setAnchorEl(null);
    };

    return (
        <>
            {isXSmall ? (
                <Tooltip title={buttonLabel}>
                    <IconButton
                        aria-label={buttonLabel}
                        color="primary"
                        onClick={event => setAnchorEl(event.currentTarget)}
                        size="large"
                    >
                        <SortIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Button
                    aria-controls="member-sort-menu"
                    aria-haspopup="true"
                    color="primary"
                    onClick={event => setAnchorEl(event.currentTarget)}
                    startIcon={<SortIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    size="small"
                >
                    {buttonLabel}
                </Button>
            )}
            <Menu
                id="member-sort-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                {[
                    'lastName',
                    'firstName',
                    'lastSeen.nullslast',
                    'updatedAt',
                ].map(field => (
                    <MenuItem
                        key={field}
                        data-sort={field}
                        onClick={handleChangeSort}
                    >
                        {translateLabel({
                            resource,
                            source: sortFieldBaseName(field),
                        })}{' '}
                        {translate(
                            `ra.sort.${
                                sort.field === field
                                    ? inverseOrder(sort.order)
                                    : 'ASC'
                            }`
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
